import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Mail, Phone, Edit, Trash2, Wrench, User, Shield } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Technicians() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    specialty: "",
    password: "tech123",
    permissions: {
      customers: "full",
      work_orders: "full",
      appointments: "own_only",
      quotes: "full",
      reports: "no_access"
    }
  });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (error) {
      toast.error("Failed to fetch technicians");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTechnician) {
        await axios.put(`${API}/technicians/${editingTechnician.id}`, formData);
        toast.success("Technician updated successfully");
      } else {
        await axios.post(`${API}/technicians`, formData);
        toast.success("Technician created successfully");
      }
      setOpen(false);
      resetForm();
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save technician");
    }
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setFormData({
      name: technician.name,
      email: technician.email,
      phone: technician.phone,
      specialty: technician.specialty,
      password: "tech123",
      permissions: technician.permissions || {
        customers: "full",
        work_orders: "full",
        appointments: "own_only",
        quotes: "full",
        reports: "no_access"
      }
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this technician?")) return;
    try {
      await axios.delete(`${API}/technicians/${id}`);
      toast.success("Technician deleted successfully");
      fetchTechnicians();
    } catch (error) {
      toast.error("Failed to delete technician");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      specialty: "",
      password: "tech123",
      permissions: {
        customers: "full",
        work_orders: "full",
        appointments: "own_only",
        quotes: "full",
        reports: "no_access"
      }
    });
    setEditingTechnician(null);
  };

  const updatePermission = (key, value) => {
    setFormData({
      ...formData,
      permissions: { ...formData.permissions, [key]: value }
    });
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Technicians</h1>
          <p className="text-slate-600">Manage your technical staff</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg">
                <Plus className="w-4 h-4 mr-2" />
                Add Technician
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTechnician ? "Edit Technician" : "Add New Technician"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="specialty">Specialty *</Label>
                    <Input
                      id="specialty"
                      value={formData.specialty}
                      onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                {!editingTechnician && (
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                )}

                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <Label className="text-base font-semibold">Access Permissions</Label>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">Customers</Label>
                      <Select value={formData.permissions.customers} onValueChange={(value) => updatePermission('customers', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full Access (Create, View, Edit)</SelectItem>
                          <SelectItem value="view_only">View Only</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Work Orders</Label>
                      <Select value={formData.permissions.work_orders} onValueChange={(value) => updatePermission('work_orders', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full Access</SelectItem>
                          <SelectItem value="view_only">View Only</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Appointments</Label>
                      <Select value={formData.permissions.appointments} onValueChange={(value) => updatePermission('appointments', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">View All Appointments</SelectItem>
                          <SelectItem value="own_only">Own Appointments Only</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Expenses</Label>
                      <Select value={formData.permissions.expenses || 'no_access'} onValueChange={(value) => updatePermission('expenses', value)}>
                        <SelectTrigger data-testid="permission-expenses-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full Access</SelectItem>
                          <SelectItem value="own_only">Own Expenses Only (needs review)</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Quotes</Label>
                      <Select value={formData.permissions.quotes} onValueChange={(value) => updatePermission('quotes', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full Access</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Reports</Label>
                      <Select value={formData.permissions.reports} onValueChange={(value) => updatePermission('reports', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View Access</SelectItem>
                          <SelectItem value="no_access">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                    {editingTechnician ? "Update" : "Create"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : technicians.length === 0 ? (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <User className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No technicians yet</h3>
            <p className="text-slate-500">Add your first technician to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {technicians.map((technician) => (
            <Card key={technician.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Wrench className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{technician.name}</CardTitle>
                      <Badge className="mt-1 bg-green-100 text-green-700 hover:bg-green-100">
                        {technician.specialty}
                      </Badge>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(technician)}
                        className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(technician.id)}
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center text-sm text-slate-600">
                  <Mail className="w-4 h-4 mr-2 text-blue-600" />
                  <span className="truncate">{technician.email}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Phone className="w-4 h-4 mr-2 text-green-600" />
                  <span>{technician.phone}</span>
                </div>
                
                {technician.permissions && (
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center text-xs font-semibold text-gray-700 mb-2">
                      <Shield className="w-3 h-3 mr-1" />
                      Permissions
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {technician.permissions.customers !== 'no_access' && (
                        <Badge variant="outline" className="text-xs">Customers</Badge>
                      )}
                      {technician.permissions.work_orders !== 'no_access' && (
                        <Badge variant="outline" className="text-xs">Work Orders</Badge>
                      )}
                      {technician.permissions.appointments !== 'no_access' && (
                        <Badge variant="outline" className="text-xs">Appointments</Badge>
                      )}
                      {technician.permissions.quotes !== 'no_access' && (
                        <Badge variant="outline" className="text-xs">Quotes</Badge>
                      )}
                      {technician.permissions.reports === 'view' && (
                        <Badge variant="outline" className="text-xs">Reports</Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
